import type { FilterMenuValue } from "~/components/filter/FilterMenuTypes";
import type { ISchema, IUserEntry } from "~/models/IUserEntry";
import type { ISubscribedChannel } from "~/models/teamMode/IChannel";
import DropDownSchemaIcon from "~/components/teamMode/icons/DropDownSchemaIcon.vue";
import ClipboardIcon from "~/components/icons/ClipboardIcon.vue";

type HasTypeField = { type: string };
export type EntryFilterFunction<T extends HasTypeField> = (entry: T) => boolean;

export type SchemaFilterValue<T extends HasTypeField> = {
  filter: EntryFilterFunction<T> | null;
};

export type SchemaFilterItem<T extends HasTypeField> = FilterMenuValue<
  SchemaFilterValue<T>
>;

export function useSchemaFilters<T extends HasTypeField>(
  loggedIn: MaybeRefOrGetter<boolean>,
  subscribedChannels: MaybeRefOrGetter<ISubscribedChannel[]>
) {
  const { colorFromEntry } = useColorFromEntry();
  const makeFilterFn = (fn: (schema: ISchema) => boolean) => (entry: T) =>
    entry.type === "schema" && fn(entry as unknown as ISchema);

  const curatedFilter = computed<SchemaFilterItem<T>>(() => ({
    icon: markRaw(ClipboardIcon),
    colorClass: colorFromEntry({
      type: "schema",
      curated: 1,
    } as IUserEntry),
    key: "lexmea_schemas",
    value: {
      filter: makeFilterFn((schema) => Boolean(schema.curated)),
    },
  }));

  const ownFilter = computed<SchemaFilterItem<T>>(() => ({
    icon: markRaw(ClipboardIcon),
    colorClass: colorFromEntry({
      type: "schema",
      curated: 0,
    } as IUserEntry),
    key: "own_schemas",
    value: {
      filter: makeFilterFn((schema) => Boolean(schema.is_owned)),
    },
    disabled: !toValue(loggedIn),
    tooltip: !toValue(loggedIn)
      ? "Logge dich ein, um eigene Schemata zu sehen."
      : undefined,
  }));

  const channelFilters = useArrayMap<ISubscribedChannel, SchemaFilterItem<T>>(
    subscribedChannels,
    (channel) => ({
      title: channel.name,
      icon: markRaw(DropDownSchemaIcon),
      colorClass: channel.subscription.color,
      key: `channel_${channel.id}`,
      value: {
        filter: makeFilterFn((schema) =>
          Boolean(schema.from_channels?.some((c) => c.id === channel.id))
        ),
      },
    })
  );

  return {
    curatedFilter,
    ownFilter,
    channelFilters,
  };
}
